import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { Order } from '../../../model/interfaces/order';
import { OrderService, LotteryService } from 'src/app/services';
import { LotteryClass } from 'src/app/model/interfaces/lottery-class';
import { Subscription } from 'rxjs';
import { CheckoutState } from 'src/app/model/enums/checkout-state';
import { PriceService } from 'src/app/services/price.service';
import { ReceiptService } from 'src/app/services/receipt-service';
import { PaymentInterval } from '../../../model/types/payment-interval';


export interface PlayingMonth
{
    name: string;
    active: false | true | 'highlighted';
}

@Component({
    selector: 'app-playing-months',
    templateUrl: './playing-months.component.html',
    styleUrls: ['./playing-months.component.scss']
})
export class PlayingMonthsComponent implements OnInit, OnDestroy
{
    @Input() public checkoutState: CheckoutState;
    private drawDateFirst: Date;
    private drawDateLast: Date;
    private cardClass: LotteryClass;
    private bsClass: LotteryClass;
    public order: Order;
    private classCount: PaymentInterval;
    public playingMonths: Array<PlayingMonth> = [];
    public cardLastSale: Date;
    public infoVariant: boolean;
    public isBsAppActive: boolean;
    public orderTotalPrice: number;
    public fmtTotalPrice: number;
    public fmtOptional = false;

    private readonly months: Array<PlayingMonth> = [
        { name: 'Jan', active: false }, { name: 'Feb', active: false }, { name: 'Mar', active: false },
        { name: 'Apr', active: false }, { name: 'Maj', active: false }, { name: 'Jun', active: false },
        { name: 'Jul', active: false }, { name: 'Aug', active: false }, { name: 'Sep', active: false },
        { name: 'Okt', active: false }, { name: 'Nov', active: false }, { name: 'Dec', active: false },
    ];

    private readonly orderService: OrderService;
    private readonly lotteryService: LotteryService;
    private onOrderUpdatedSubscription: Subscription;
    private priceSubscription: Subscription;
    private readonly priceService: PriceService;
    private readonly receiptService: ReceiptService;

    constructor(
        orderService: OrderService,
        lotteryService: LotteryService,
        priceService: PriceService,
        receiptService: ReceiptService
    )
    {
        this.orderService = orderService;
        this.lotteryService = lotteryService;
        this.priceService = priceService;
        this.receiptService = receiptService;
    }

    public async ngOnInit(): Promise<void>
    {
        await this.getOrder();
        await Promise.all([this.getCardDates(), this.getBsDates()]);
        await this.setClassCount();
        this.setDrawDates();
        this.getPlayingMonths();
        this.setActivePlayingMonths();
        this.orderUpdated();
    }

    private async getCardDates(): Promise<void>
    {
        if (this.order.FMTClassId !== 0)
        {
            this.lotteryService.getLotteryClassById(this.order.FMTClassId)
                .subscribe((lotteryClass: LotteryClass) =>
                {
                    this.cardClass = lotteryClass;
                    this.cardLastSale = new Date(lotteryClass.DankortLastSale);
                });
        }
    }

    private getBsDates()
    {
        if (this.order.SubscriptionStartClassId !== 0)
        {
            this.lotteryService.getLotteryClassById(this.order.SubscriptionStartClassId)
                .subscribe((lotteryClass: LotteryClass) =>
                {
                    this.bsClass = lotteryClass;
                });
        }
    }

    // Sets drawdates
    private setDrawDates(): void
    {
        if (this.cardClass != null)
        {
            this.drawDateFirst = new Date(this.cardClass.Draws[0].DrawDate); // First date should always be from card since we want to show it
        }
        else
        {
            this.drawDateFirst = new Date(this.bsClass.Draws[0].DrawDate); // First date should always be from card since we want to show it
        }

        if (this.cardClass != null && this.cardClass.Draws[0].DrawDate != this.bsClass.Draws[0].DrawDate)
        {
            this.drawDateLast = new Date(this.drawDateFirst.getFullYear(), this.drawDateFirst.getMonth() + 6, this.drawDateFirst.getDate() + 4);
        }
        else
        {
            this.drawDateLast = new Date(this.drawDateFirst.getFullYear(), this.drawDateFirst.getMonth() + 5, this.drawDateFirst.getDate() + 4);
        }
    }

    // Subscribe to order and listen for updates.
    private orderUpdated(): void
    {
        this.onOrderUpdatedSubscription = this.orderService.onOrderContentUpdated
            .subscribe(() =>
            {
                this.onOrderUpdated();
            });
    }

    // What happens whenever order content gets updated:
    private async onOrderUpdated(): Promise<void>
    {
        await this.getOrder();
        this.setClassCount();
        this.setActivePlayingMonths();
        this.fmtOptional = (this.playingMonths.length === 7 && this.cardLastSale != null && this.checkoutState !== 1) ;

        if(this.isBsAppActive){
            this.fmtOptional = true;
        }
    }

    // Populates playingMonths array with the months between drawDateFirst and drawDateLast.
    private getPlayingMonths(): void
    {
        this.playingMonths = [];

        const firstDrawMonth = this.drawDateFirst.getMonth();
        const lastDrawMonth = this.drawDateLast.getMonth();
        if (lastDrawMonth <= firstDrawMonth)
        {
            this.playingMonths = [...this.months.slice(firstDrawMonth), ...this.months.slice(0, lastDrawMonth + 1)];
        } else
        {
            this.playingMonths = this.months.slice(firstDrawMonth, lastDrawMonth + 1);
        }
    }

    private async getOrder(): Promise<void>
    {
        if (this.checkoutState === CheckoutState.Payment)
        {
            this.order = this.receiptService.getOrders().orderSubscription;
        } else
        {
            this.order = await this.orderService.getOrder(false);
        }
        // calculate totalprice of order
        this.getTotalPrices();
    }

    private getTotalPrices(): void
    {
        if(this.priceSubscription != null)
        {
            this.priceSubscription.unsubscribe();
        }        

        this.priceSubscription = this.priceService.getPriceByOrder(this.order).subscribe(priceData => {
            this.orderTotalPrice = priceData.totalPrice + this.priceService.getTotalFees(this.order);
            this.fmtTotalPrice = priceData.fmtPrice;
        });
    }

    private setClassCount(): void
    {
        this.classCount = this.order.NumberOfClasses;
    }

    private resetActivePlayingMonths(): void
    {
        if (this.playingMonths.length)
        {
            for (let i = 0; i < this.playingMonths.length; i++)
            {
                this.playingMonths[i].active = false;
            }
        }
    }

    // Sets active flag for playingMonths.
    private setActivePlayingMonths(): void
    {
        this.resetActivePlayingMonths();

        if (this.playingMonths.length === 0)
        {
            return;
        }

        // PlayingMonth can either be of .lenght 7 or 6, depending on wether you'll be able to participate in the current month.
        // If playingMonths contains 7 months and classCount is 1, then set the second playingMonth to active.
        // Else we can assume that playingMonths contain 6 months and then we set all 6 months to active.
        if (this.playingMonths.length === 7)
        {
            if (this.classCount === 1)
            {
                this.playingMonths[1].active = true;
            } else
            {
                for (let i = 1; i < this.playingMonths.length; i++)
                {
                    this.playingMonths[i].active = true;
                }
            }
        }

        // If playingMonths contains 6 months and classCount is 1, then set the first playingMonth to active.
        // Else we can assume that classCount is set to 6 months and then we set all 6 months to active.
        if (this.playingMonths.length === 6)
        {
            if (this.classCount === 1)
            {
                this.playingMonths[0].active = true;
            }
            else
            {
                for (let i = 0; i < this.playingMonths.length; i++)
                {
                    this.playingMonths[i].active = true;
                }
            }
        }

        // Set first playingMonth to active when we know we are at creditcard payment.
        // And set active months that are true to be "highlighted", so that they are only
        // semi-highlighted at this step.
        if (this.checkoutState === 3 || this.isBsAppActive)
        {
            for (let i = 0; i < this.playingMonths.length; i++)
            {
                if (this.playingMonths[i].active)
                {
                    this.playingMonths[i].active = 'highlighted';
                }
            }
            this.playingMonths[0].active = true;
        }
    }

    public ngOnDestroy(): void
    {
        this.onOrderUpdatedSubscription.unsubscribe();
        this.priceSubscription.unsubscribe();
    }
}
